'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { FaPlay } from 'react-icons/fa6';
import { getCEOVideo } from '@/src/lib/sanity.queries';
import CEOImage from '@/public/CEOImage.png';
import CEOImageBG from '@/public/CEOImageBG.png';

export default function CEOWordSection({ locale }: { locale: string }) {
  const t = useTranslations('Home.CEOWordSection');
  const isRTL = locale === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [isOpen, setIsOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    async function fetchVideo() {
      const result = await getCEOVideo();
      setVideoUrl(result.videoUrl);
    }
    fetchVideo();
  }, []);

  return (
    <section
      dir={dir}
      className="relative bg-white pt-16 pb-16 text-white md:pt-24 md:pb-24 lg:pt-32 lg:pb-32">
      {/* Background image - responsive positioning */}
      <Image
        src={CEOImageBG}
        alt="CEO Image BG"
        className="absolute inset-0 z-20 mx-auto hidden h-full max-w-[998px] object-contain lg:block"
      />

      {/* Mobile and Desktop Layout */}
      <div className="] mx-auto w-full max-w-[1440px] px-4 sm:px-6 md:px-8 lg:px-16 xl:px-32">
        {/* Main purple rounded rectangle */}
        <div className="relative min-h-[406px] w-full rounded-2xl bg-[#561952] p-4 md:rounded-4xl md:p-6 lg:p-8">
          {/* Content Layout */}
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-10 md:px-6 md:pt-6 lg:px-10 lg:pt-8">
            {/* Text Content */}
            <div className="z-30 flex w-full max-w-full flex-col gap-2 md:max-w-[400px] lg:max-w-[534px]">
              <p className="text-2xl leading-tight text-white sm:text-3xl md:text-4xl lg:text-[50px]">
                {t('Title')}
              </p>
              {/* <p className="text-sm text-white">{t('Desc')}</p> */}
            </div>

            {/* CEO Image - responsive positioning */}
            <div className="relative w-full max-w-[430px] p-0 md:absolute md:bottom-0 md:z-20 md:flex md:items-center md:justify-center ltr:md:right-8 lg:ltr:right-16 rtl:md:left-8 lg:rtl:left-16">
              <Image
                src={CEOImage}
                alt="CEO Image"
                className="h-auto w-full object-contain md:h-full md:w-full"
              />
              <p className="absolute bottom-4 left-1 rounded-2xl bg-black/20 px-3 py-2 backdrop-blur-lg md:bottom-8 md:rounded-3xl md:px-5 md:py-2">
                <p className="text-lg text-white md:text-2xl lg:text-4xl">{t('AhmedJlidan')}</p>
              </p>
            </div>
          </div>

          {/* Play Button - responsive positioning */}
          <div className="relative z-40 mt-6 flex cursor-pointer gap-4 md:absolute md:bottom-6 md:gap-8 lg:bottom-10">
            <button
              type="button"
              className="flex cursor-pointer items-center gap-2 rounded-full border border-[#FFD400] text-white"
              onClick={() => setIsOpen(true)}>
              <p className="text-xs font-bold text-white md:text-sm ltr:pl-2 md:ltr:pl-3 rtl:pr-2 md:rtl:pr-3">
                {t('PlayVideos')}
              </p>
              <p className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFD400] lg:h-16 lg:w-16">
                <FaPlay className="h-2 w-2 md:h-3 md:w-3" />
              </p>
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85">
          <div className="relative mx-4 w-full max-w-6xl rounded-2xl bg-white p-6 shadow-xl sm:p-8 md:p-10">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="hover:bg-primary-500/80 bg-primary-500 hover:bg-primary-700 absolute top-4 right-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-xl font-bold text-white"
              aria-label={t('close')}>
              ×
            </button>
            {videoUrl ? (
              <>
                <video
                  controls
                  autoPlay
                  playsInline
                  controlsList="nofullscreen nodownload noremoteplayback noplaybackrate foobar"
                  disablePictureInPicture
                  className="mt-4 h-auto w-full rounded-lg"
                  src={videoUrl}
                />
              </>
            ) : (
              <div className="py-10 text-center">{t('loadingVideo')}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
